import { FC, ReactNode, useState, useCallback } from "react"
import { useParams, Link } from "react-router-dom"
import {
  Building2,
  Pencil,
  Palette,
  Mail,
  FileText,
  Star,
  ChevronDown,
  ChevronUp,
  ArrowUpRight,
  Users,
  AlertTriangle,
  Compass
} from "lucide-react"
import BreadCrumbs from "../../common/ui/breadcrumbs"
import ManageBrand from "../../common/ui/brands/manage"
import BrandAdminsList from "../../common/ui/brand-admin/brand-admins-list"
import BBSwitch from "../../common/ui/form/Switch"
import { BadgeSuccess, BadgeDanger } from "../../common/ui/badge"
import { If, LightBtn } from "../../common/ui"
import { BrandDetailsSkeleton } from "../../common/ui/skeletons"
import { RequireRole } from "../../common/validation"
import { useFetchBrandQuery, useUpdateBrandMutation } from "../../redux/queries"
import { useAppDispatch } from "../../common/hooks"
import { pushToast } from "../../redux/slices/notifications"
import { Brand } from "../../types"

/* ─── Reusable sub-components ────────────────────────────────────── */

const Section: FC<{
  icon: ReactNode
  title: string
  children: ReactNode
  action?: ReactNode
  className?: string
  accent?: string
}> = ({
  icon,
  title,
  children,
  action,
  className = "",
  accent = "#5016a3"
}) => (
  <div
    className={`bg-white rounded-lg border border-gray-200 overflow-hidden ${className}`}
    style={{ borderLeftWidth: 3, borderLeftColor: accent }}
  >
    <div className="flex items-center justify-between px-5 pt-5 pb-3">
      <div className="flex items-center gap-2">
        <span className="text-primary-400">{icon}</span>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
          {title}
        </h3>
      </div>
      {action}
    </div>
    <div className="px-5 pb-5">{children}</div>
  </div>
)

const Row: FC<{ label: string; value?: ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between items-start py-2.5 border-b border-gray-100 last:border-0">
    <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">
      {label}
    </span>
    <span className="text-sm text-gray-800 text-right max-w-[65%] break-all">
      {value || <span className="text-gray-300">&mdash;</span>}
    </span>
  </div>
)

const ColorSwatch: FC<{ label: string; color?: string }> = ({
  label,
  color
}) => (
  <div className="flex items-center gap-3">
    <div
      className="w-8 h-8 rounded border border-gray-200 shrink-0"
      style={{ backgroundColor: color || "#f3f4f6" }}
    />
    <div>
      <div className="text-xs text-gray-400">{label}</div>
      <div className="text-sm font-mono text-gray-700">
        {color || "Not set"}
      </div>
    </div>
  </div>
)

const DisclaimerItem: FC<{
  label: string
  value?: string
  isOpen: boolean
  onToggle: () => void
}> = ({ label, value, isOpen, onToggle }) => (
  <div className="border-b border-gray-100 last:border-0">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between py-3 text-left hover:bg-gray-50 transition-colors rounded"
    >
      <span className="text-sm text-gray-700">{label}</span>
      <div className="flex items-center gap-2">
        {!value && <span className="text-xs text-gray-300">empty</span>}
        {isOpen ? (
          <ChevronUp size={14} className="text-gray-400" />
        ) : (
          <ChevronDown size={14} className="text-gray-400" />
        )}
      </div>
    </button>
    {isOpen && (
      <div className="pb-3 text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
        {value || (
          <span className="text-gray-300 italic">No disclaimer set</span>
        )}
      </div>
    )}
  </div>
)

const QuickLink: FC<{ to: string; label: string }> = ({ to, label }) => (
  <Link
    to={to}
    className="flex items-center justify-between py-2.5 px-3 rounded-md hover:bg-primary-50 transition-colors group border-b border-gray-50 last:border-0"
  >
    <span className="text-sm text-gray-700 group-hover:text-primary-400">
      {label}
    </span>
    <ArrowUpRight
      size={14}
      className="text-gray-300 group-hover:text-primary-400"
    />
  </Link>
)

/* ─── Helpers ────────────────────────────────────────────────────── */

const formatTimestamp = (ts?: string) => {
  if (!ts) return undefined
  const n = parseInt(ts, 10)
  if (isNaN(n)) return undefined
  return new Date(n * 1000).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  })
}

/* ─── Page ───────────────────────────────────────────────────────── */

const BrandManagePage = () => {
  const { brandId } = useParams()
  const { data: brand, isFetching } = useFetchBrandQuery(brandId ?? "", {
    skip: !brandId
  })
  const [updateBrand] = useUpdateBrandMutation()
  const dispatch = useAppDispatch()

  const [editOpen, setEditOpen] = useState(false)
  const [expandedDisclaimers, setExpandedDisclaimers] = useState<string[]>([])

  const toggleDisclaimer = (id: string) => {
    setExpandedDisclaimers(prev =>
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    )
  }

  const handleToggle = useCallback(
    async (field: keyof Brand, currentValue: boolean) => {
      if (!brand) return
      try {
        await updateBrand({ ...brand, [field]: !currentValue }).unwrap()
        dispatch(
          pushToast({ msg: "Setting updated successfully", timeout: 3000 })
        )
      } catch {
        dispatch(
          pushToast({
            msg: "Failed to update setting",
            timeout: 3000,
            theme: "danger"
          })
        )
      }
    },
    [brand, updateBrand, dispatch]
  )

  if (!brand?.id || !brandId)
    return (
      <If
        condition={!isFetching}
        then={<div className="p-6">Brand not found</div>}
        else={<BrandDetailsSkeleton />}
      />
    )

  const disclaimers = [
    { id: "general", label: "General disclaimer", value: brand.disclaimer },
    {
      id: "payment",
      label: "Payment details",
      value: brand.payment_details_disclaimer
    },
    {
      id: "loan-calc",
      label: "Loan calculator",
      value: brand.loan_calc_disclaimer
    },
    {
      id: "lead",
      label: "Lead capture",
      value: brand.lead_capture_disclaimer
    },
    {
      id: "rate-alert",
      label: "Rate alert",
      value: brand.rate_alert_disclaimer
    },
    {
      id: "subscription",
      label: "Subscription email",
      value: brand.subscription_email_disclaimer
    },
    {
      id: "third-party",
      label: "Third party fees",
      value: brand.third_party_fees_disclaimer
    }
  ]

  return (
    <RequireRole allowedRoles={["administrator"]}>
      <div className="min-h-full bg-gray-50">
        {/* ── Header ─────────────────────────────────────────── */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-6 pt-5 pb-6">
            <BreadCrumbs
              pages={[
                { name: "Brands", href: "/brands" },
                { name: brand.name, href: `/brands/${brand.id}` },
                { name: "Manage", href: "#", current: true }
              ]}
            />

            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                {brand.logo ? (
                  <div className="h-14 w-14 rounded-lg border border-gray-200 p-2 bg-white shrink-0 flex items-center justify-center">
                    <img
                      className="max-h-full max-w-full object-contain"
                      src={brand.logo}
                      alt={brand.name}
                    />
                  </div>
                ) : (
                  <div className="h-14 w-14 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                    <Building2 size={24} className="text-primary-400" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-xl font-serif font-bold text-gray-900">
                      {brand.name}
                    </h1>
                    {brand.disabled ? (
                      <BadgeDanger>Disabled</BadgeDanger>
                    ) : (
                      <BadgeSuccess>Active</BadgeSuccess>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                    <span>ID: {brand.id}</span>
                    {brand.nmls && (
                      <>
                        <span className="text-gray-300">&bull;</span>
                        <span>NMLS: {brand.nmls}</span>
                      </>
                    )}
                    {brand.website && (
                      <>
                        <span className="text-gray-300">&bull;</span>
                        <a
                          href={
                            brand.website.startsWith("http")
                              ? brand.website
                              : `https://${brand.website}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-400 hover:underline inline-flex items-center gap-1"
                        >
                          {brand.website}
                          <ArrowUpRight size={12} />
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <LightBtn onClick={() => setEditOpen(true)}>
                <Pencil size={14} className="mr-1.5" />
                Edit Brand
              </LightBtn>
            </div>
          </div>
        </div>

        {/* ── Content Grid ───────────────────────────────────── */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Brand Profile */}
            <Section icon={<Building2 size={18} />} title="Profile">
              <Row label="Name" value={brand.name} />
              <Row label="Website" value={brand.website} />
              <Row label="NMLS" value={brand.nmls} />
              <Row label="Org ID" value={brand.org_id} />
              <Row label="Non-bank" value={brand.non_bank ? "Yes" : "No"} />
              <Row label="Target price" value={brand.target_price} />
              <Row label="Created" value={formatTimestamp(brand.created)} />
              <Row label="Modified" value={formatTimestamp(brand.changed)} />
            </Section>

            {/* Branding */}
            <Section icon={<Palette size={18} />} title="Branding">
              <div className="space-y-4">
                <ColorSwatch label="Primary" color={brand.primary_color} />
                <ColorSwatch label="Secondary" color={brand.secondary_color} />
              </div>
              {brand.logo && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="text-xs text-gray-400 mb-2">Logo</div>
                  <div className="h-12 bg-gray-50 rounded p-2 inline-flex items-center">
                    <img
                      className="h-full object-contain"
                      src={brand.logo}
                      alt="Brand logo"
                    />
                  </div>
                </div>
              )}
            </Section>

            {/* Email Preferences */}
            <Section icon={<Mail size={18} />} title="Email Preferences">
              <div className="space-y-4">
                <BBSwitch
                  label="Subscription emails"
                  description="Send subscription notification emails"
                  enabled={!brand.disable_subscription_emails}
                  setEnabled={() =>
                    handleToggle(
                      "disable_subscription_emails",
                      brand.disable_subscription_emails
                    )
                  }
                />
                <BBSwitch
                  label="Receipt emails"
                  description="Send receipt emails for payments"
                  enabled={!brand.disable_receipt_emails}
                  setEnabled={() =>
                    handleToggle(
                      "disable_receipt_emails",
                      brand.disable_receipt_emails
                    )
                  }
                />
                <BBSwitch
                  label="Itemized fees"
                  description="Show itemized fee breakdown"
                  enabled={!brand.disable_itemized_fees}
                  setEnabled={() =>
                    handleToggle(
                      "disable_itemized_fees",
                      brand.disable_itemized_fees
                    )
                  }
                />
              </div>
            </Section>

            {/* Review Platforms */}
            <Section icon={<Star size={18} />} title="Review Platforms">
              <Row label="Yelp ID" value={brand.yelp_id} />
              <Row label="BirdEye ID" value={brand.birdeye_id} />
              <Row label="Experience ID" value={brand.experience_id} />
              <Row label="Zillow brand" value={brand.zillow_brand_name} />
              <Row label="Google Place" value={brand.place_id} />
            </Section>

            {/* Brand Admins */}
            <Section icon={<Users size={18} />} title="Brand Admins">
              <BrandAdminsList brandId={brandId} canMasquerade />
            </Section>

            {/* Quick Navigation */}
            <Section icon={<Compass size={18} />} title="Quick Links">
              <QuickLink
                to={`/brands/${brand.id}?tab=brand-details`}
                label="Brand Details"
              />
              <QuickLink
                to={`/brands/${brand.id}?tab=integrations`}
                label="Integrations"
              />
              <QuickLink
                to={`/brands/${brand.id}?tab=loan-officers`}
                label="Loan Officers"
              />
              <QuickLink to={`/brands/${brand.id}?tab=leads`} label="Leads" />
              <QuickLink
                to={`/brands/${brand.id}?tab=subscriptions`}
                label="Subscriptions"
              />
              <QuickLink
                to={`/brands/${brand.id}?tab=pricing-log`}
                label="Pricing Log"
              />
              <QuickLink
                to={`/config-admin?tab=connectors&brand_id=${brand.id}`}
                label="Connectors"
              />
              <QuickLink
                to={`/config-admin?tab=rateflow&brand_id=${brand.id}`}
                label="Pricing Engine Config"
              />
              {brand.org_id && (
                <QuickLink to={`/billing/${brand.org_id}`} label="Billing" />
              )}
            </Section>

            {/* Disclaimers — full width */}
            <Section
              icon={<FileText size={18} />}
              title="Disclaimers"
              className="lg:col-span-2"
              action={
                <button
                  type="button"
                  onClick={() =>
                    setExpandedDisclaimers(prev =>
                      prev.length === disclaimers.length
                        ? []
                        : disclaimers.map(d => d.id)
                    )
                  }
                  className="text-xs text-primary-400 hover:text-primary-300 font-medium"
                >
                  {expandedDisclaimers.length === disclaimers.length
                    ? "Collapse all"
                    : "Expand all"}
                </button>
              }
            >
              {disclaimers.map(d => (
                <DisclaimerItem
                  key={d.id}
                  label={d.label}
                  value={d.value}
                  isOpen={expandedDisclaimers.includes(d.id)}
                  onToggle={() => toggleDisclaimer(d.id)}
                />
              ))}
            </Section>

            {/* Danger Zone — full width */}
            <Section
              icon={<AlertTriangle size={18} />}
              title="Danger Zone"
              className="lg:col-span-2"
              accent="#D96060"
            >
              <div className="flex items-center justify-between py-2">
                <div>
                  <div className="text-sm font-medium text-gray-700">
                    Brand status
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {brand.disabled
                      ? "This brand is currently disabled. Enable it to restore access."
                      : "Disabling this brand will hide it from public-facing pages."}
                  </div>
                </div>
                <BBSwitch
                  enabled={!brand.disabled}
                  setEnabled={() => handleToggle("disabled", brand.disabled)}
                />
              </div>
            </Section>
          </div>
        </div>

        {/* ── Edit Side Panel ────────────────────────────────── */}
        <ManageBrand
          open={editOpen}
          data={brand}
          isInSidePanel
          onClose={() => setEditOpen(false)}
        />
      </div>
    </RequireRole>
  )
}

export default BrandManagePage
